<template>
	<div class="content-page content-page--roles">
		<div class="content-page__heading" ref="rolesContentPageHeading">
			<h2 class="content-page__title">Manage Roles</h2>
			<div class="content-page__actions">
				<ButtonGroup
					:disabled="!addRoleDisabled"
					:title="
						!addRoleDisabled
							? 'You don\'t have permission to perform this action.'
							: 'Select to go to add role.'
					"
				>
					<template #container>
						<Button
							:id="`${uid}_add`"
							className="btn--has-icon"
							iconClassName="icon-more"
							labelText="Add Consultant Role"
							:btnClick="
								() => {
									addRole('vue-support-external');
								}
							"
							:iconBeforeLabel="true"
							:disabled="!addRoleDisabled"
							:title="
								!addRoleDisabled
									? 'You don\'t have permission to perform this action.'
									: 'Select to go to add role.'
							"
						/>

						<Button
							:id="`${uid}_add`"
							className="btn--has-icon"
							iconClassName="icon-more"
							labelText="Add Internal Role"
							:btnClick="
								() => {
									addRole('vue-support-portal');
								}
							"
							:iconBeforeLabel="true"
							:disabled="!addRoleDisabled"
							:title="
								!addRoleDisabled
									? 'You don\'t have permission to perform this action.'
									: 'Select to go to add role.'
							"
						/>
					</template>
				</ButtonGroup>
			</div>
		</div>
		<div
			class="results-toolbar results-toolbar--variant-three"
			ref="roleResultsToolBar"
		>
			<RoleFilters v-model="filters" :defaultFilters="defaultFilters" />
			<div class="results-toolbar__counter">
				<ResultCounter :counter="resultCounter" v-if="hasResults" />
			</div>
		</div>
		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<RolesList :items="rolesList" ref="rolesList" />
		</ScrollContainer>
		<Spinner v-if="loading || loadingRole" :processor="{ loading: true }" />
		<NoResults v-if="showNoResults" />
		<RoleAddEditFlyout
			v-model="showRoleFlyout"
			:roleData="role"
			@toast="updateToast"
		/>
	</div>
</template>

<script>
import ResultCounter from '@/site-components/result-counter/result-counter';
import Button from '@/components/forms/button/button';
import RoleAddEditFlyout from '../settings/role-add-edit-flyout';
import { mapActions, mapGetters } from 'vuex';
import RolesList from '@/sections/roles/summary/roles-list.vue';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
import ButtonGroup from '@/components/forms/button/button-group';
import { getDifferentValues } from '@/utils/function';
import RoleFilters from '@/sections/roles/summary/roles-filters.vue';
import _ from 'lodash';

export default {
	name: 'Roles',
	components: {
		ResultCounter,
		ScrollContainer,
		Button,
		RoleAddEditFlyout,
		RolesList,
		Spinner,
		NoResults,
		ButtonGroup,
		RoleFilters
	},
	props: {
		count: {
			type: Number
		},
		sortBy: {
			type: String
		},
		sortDirection: {
			type: String
		},
		search: {
			type: String
		},
		roleId: {
			type: String
		},
		active: {
			type: String
		},
		clientId: {
			type: String
		}
	},
	data() {
		return {
			filters: {
				sortby: this.sortBy,
				sortdirection: this.sortDirection,
				active: this.active,
				clientId: this.clientId,
				search: this.search
			},
			defaultFilters: {
				sortby: 'role',
				sortdirection: 'asc',
				active: null,
				clientId: null,
				search: ''
			},
			page: 1,
			countPerPage: this.count,
			showRoleFlyout: false,
			role: {},
			roleIdData: 0,
			loading: false,
			loadingMore: false,
			ddStatusOptions: [
				{
					text: 'All Status',
					value: null
				},
				{
					text: 'Active',
					value: 'true'
				},
				{
					text: 'Inactive',
					value: 'false'
				}
			],
			ddRoleTypesOptions: [
				{
					text: 'All Role Types',
					value: null
				},
				{
					text: 'Consultant Role',
					value: 'vue-support-external'
				},
				{
					text: 'Internal Role',
					value: 'vue-support-portal'
				}
			],
			loadingRole: false
		};
	},
	beforeRouteEnter(to, from, next) {
		if (to.name === 'roles') {
			to.meta.breadcrumb = [];
		}
		next();
	},
	computed: {
		...mapGetters('roles', ['roles', 'permissions', 'reloadRoles']),

		addRoleDisabled() {
			return this.validatePermission('roles.create');
		},
		rolesList() {
			return this.roles.items || [];
		},
		hasResults() {
			return this.rolesList?.length;
		},
		hasMoreItemsToLoad() {
			return _.isEmpty(this.roles)
				? false
				: this.page < this.roles.totalPageCount;
		},
		resultCounter() {
			return `${this.roles.items?.length} of ${this.roles.totalCount}`;
		},
		showNoResults() {
			return !this.loading && !this.hasResults;
		}
	},
	methods: {
		...mapActions('roles', ['loadRoles', 'loadPermissions']),
		...mapActions('roles', { getRoleAction: 'getRole' }),
		addRole(clientId) {
			this.role = { clientId };
			this.roleIdData = 0;
			this.showRoleFlyout = true;
		},
		updateToast(val) {
			this.$store.dispatch('toastMessages/show', {
				toastId: 'roles.action',
				statusCode: val.status,
				message: val.message,
				slot: 'default',
				useIcon: true,
				userDismissable: true
			});
		},
		async fetchData(page) {
			let query = _.cloneDeep(this.filters);
			query.page = page;
			query.count = this.countPerPage || 100;
			const { items } = this.permissions;
			if (!this.loadingMore) {
				this.loading = true;
			}

			try {
				if (!items) {
					await Promise.all([
						this.loadPermissions(),
						this.loadRoles(query)
					]).then(res => {
						this.parseResponse(res[1]);
					});
				} else {
					await this.loadRoles(query).then(res => {
						this.parseResponse(res);
					});
				}
			} finally {
				this.loading = false;
				this.loadingMore = false;
			}
		},
		parseResponse(data) {
			let newData = _.cloneDeep(data);

			if (!_.isEmpty(newData)) {
				newData.items.forEach(item => {
					if (item.role.includes('/')) {
						item.role = item.role.split('/').join('');
					}
				});
			}
		},
		async getRoleById() {
			if (this.roleIdData > 0) {
				const mapCustomers = (obj, propName) => {
					return obj[propName].map(item => {
						return {
							text: item.customerName ?? item, // remove after ??
							value: item.customerId ?? item // remove after ??
						};
					});
				};

				this.loadingRole = true;
				await this.getRoleAction(this.roleIdData).then(resp => {
					let roleCopy = {
						..._.omit(resp, ['customers', 'permissions']),
						customers: []
					};
					if (!_.isEmpty(resp.permissions)) {
						const permissionMapped = resp.permissions.map(
							permission => permission.permissionId
						);
						Object.assign(roleCopy, {
							permissions: permissionMapped
						});
					}

					if (!_.isEmpty(resp.customers)) {
						const customers = mapCustomers(resp, 'customers');
						Object.assign(roleCopy, {
							customers: customers
						});
					}

					this.role = roleCopy;
					this.showRoleFlyout = true;
					this.loadingRole = false;
				});
			}
		},
		updateRoute(queries) {
			let queryObj = getDifferentValues(this.defaultFilters, queries);

			if (this.showRoleFlyout) {
				if (queries.roleId) {
					queryObj.roleId = this.roleIdData;
				}
			} else {
				delete queryObj.roleId;
			}

			if (!_.isEmpty(queryObj)) {
				this.$router
					.push({
						path: '/roles',
						query: queryObj
					})
					.catch(() => {});
			} else {
				this.$router
					.push({
						path: '/roles'
					})
					.catch(() => {});
			}
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
				this.page += 1;
			}
		}
	},
	watch: {
		filters: {
			deep: true,
			handler(obj) {
				this.updateRoute(obj);
				if (this.page != 1) {
					this.page = 1;
				} else {
					this.fetchData(1);
				}
			}
		},
		page: {
			handler(page) {
				this.fetchData(page);
			}
		},
		reloadRoles: {
			handler(reload) {
				if (reload) {
					this.fetchData(1);
				}
			}
		},
		roleId: {
			immediate: true,
			async handler(val) {
				if (val) {
					if (_.isEmpty(this.rolesList)) {
						await this.fetchData(1);
					}
					this.roleIdData = parseInt(val);
					await this.getRoleById();
				}
			}
		},
		showRoleFlyout: {
			handler(val) {
				if (val && this.roleIdData > 0) {
					this.updateRoute({
						...this.filters,
						roleId: this.roleIdData
					});
				} else {
					if (!val) {
						this.role = {};
					}
					this.updateRoute(this.filters);
				}
			}
		},
		'$route.query': {
			immediate: true,
			handler(newQuery) {
				if (!newQuery.roleId) {
					let newfilters = { ...this.defaultFilters, ...newQuery };
					if (!_.isEqual(newfilters, this.filters)) {
						this.filters = _.cloneDeep(newfilters);
					}
				}
			}
		}
	},
	beforeRouteLeave(to, from, next) {
		if (to.name === 'roles-history') {
			to.meta.breadcrumb = [
				{
					name: 'Roles',
					data: { path: from.fullPath }
				},
				{ name: 'History' }
			];
			const obj = { path: to.fullPath, breadcrumb: to.meta.breadcrumb };
			this.$store.commit('setBreadCrumbs', obj);
		}
		next();
	}
};
</script>

<style lang="scss" scoped>
/**
    * [Description]
    *
    * Manage Roles Screen
    * Can use SCSS variables = TRUE
 */
</style>
